import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getUsers, grantPlan, revokePlan, setUserDisabled } from "@/api/endpoints";
import { Badge, Button, Card, Input, PageTitle } from "@/components/ui/primitives";
import { DataState } from "@/components/data-state";
import { Pagination } from "@/components/pagination";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import type { PlanTier, UserRow } from "@/types";

const PAGE_SIZE = 25;

export function UsersPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const query = useDebouncedValue(search.trim(), 300);
  const queryClient = useQueryClient();

  const list = useQuery({
    queryKey: ["system-users", query, page],
    queryFn: () => getUsers({ query, page, limit: PAGE_SIZE }),
  });

  const plan = useMutation({
    mutationFn: (input: { id: string; tier: PlanTier | null }) =>
      input.tier ? grantPlan(input.id, { tier: input.tier }) : revokePlan(input.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["system-users"] });
    },
    onError: (error: unknown) => {
      const code = (error as { response?: { data?: { error?: { code?: string } } } })
        ?.response?.data?.error?.code;
      window.alert(
        code === "STORE_SUBSCRIPTION"
          ? "Bu hesabın mağaza aboneliği var. Plan buradan değiştirilemez; App Store veya Play üzerinden yönetilmeli."
          : "Plan değiştirilemedi.",
      );
    },
  });

  const toggle = useMutation({
    mutationFn: (input: { id: string; disabled: boolean }) =>
      setUserDisabled(input.id, input.disabled),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["system-users"] });
    },
  });

  return (
    <div className="space-y-4">
      <PageTitle>Kullanıcılar</PageTitle>

      <Input
        value={search}
        placeholder="Ara"
        onChange={(event) => {
          setSearch(event.target.value);
          setPage(0);
        }}
        className="max-w-sm"
      />

      <DataState
        isLoading={list.isLoading}
        isError={list.isError}
        isEmpty={(list.data?.items.length ?? 0) === 0}
        onRetry={() => void list.refetch()}
      >
        <Card className="divide-y divide-border">
          {list.data?.items.map((user) => (
            <div key={user.id} className="flex flex-wrap items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{user.email}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {user.organizations.length === 0
                    ? "Organizasyon yok"
                    : user.organizations
                        .map((org) => `${org.name ?? org.orgId} (${org.role})`)
                        .join(" · ")}
                </p>
              </div>

              {user.disabled && <Badge tone="danger">Kapalı</Badge>}
              <PlanCell user={user} busy={plan.isPending} onChange={plan.mutate} />

              <Button
                variant={user.disabled ? "secondary" : "destructive"}
                size="sm"
                disabled={toggle.isPending}
                onClick={() => {
                  const next = !user.disabled;
                  const message = next
                    ? `${user.email} hesabı kapatılsın mı? Açık oturumları hemen sonlanır.`
                    : `${user.email} hesabı yeniden açılsın mı?`;
                  if (window.confirm(message)) {
                    toggle.mutate({ id: user.id, disabled: next });
                  }
                }}
              >
                {user.disabled ? "Aç" : "Kapat"}
              </Button>
            </div>
          ))}
        </Card>
        <Pagination
          page={page}
          limit={PAGE_SIZE}
          total={list.data?.total ?? 0}
          onChange={setPage}
        />
      </DataState>
    </div>
  );
}

/**
 * The account's plan, and the operator's handle on it.
 *
 * A store subscription is shown but not editable: the receipt is the link
 * between this account and the store's notifications, and overwriting it here
 * would break that for good. Only grants made from this panel can be changed
 * from this panel.
 */
function PlanCell({
  user,
  busy,
  onChange,
}: {
  user: UserRow;
  busy: boolean;
  onChange: (input: { id: string; tier: PlanTier | null }) => void;
}) {
  const subscription = user.subscription;
  const fromStore = subscription && subscription.platform !== "manual";
  /** A manual grant on a tier the dropdown no longer offers, if there is one. */
  const grandfathered =
    subscription && subscription.tier !== "premium" ? subscription.tier : null;

  if (fromStore) {
    return (
      <Badge tone="neutral">
        {subscription.tier} · {subscription.platform}
      </Badge>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {subscription && <Badge tone="success">{subscription.tier}</Badge>}
      <select
        value={subscription?.tier ?? ""}
        disabled={busy}
        aria-label={`${user.email} planı`}
        className="h-8 rounded-md border border-input bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onChange={(event) => {
          const value = event.target.value;
          const message = value
            ? `${user.email} hesabına ${value} planı verilsin mi? Sahip olduğu bütün organizasyonlar premium olur.`
            : `${user.email} hesabının planı kaldırılsın mı? Organizasyonları ücretsiz plana döner; hiçbir veri silinmez.`;
          if (!window.confirm(message)) {
            // Put the dropdown back; the change never happened.
            event.target.value = subscription?.tier ?? "";
            return;
          }
          onChange({ id: user.id, tier: value === "" ? null : (value as PlanTier) });
        }}
      >
        <option value="">Ücretsiz</option>
        <option value="premium">Premium</option>
        {/*
          Only `premium` is listed, because only `premium` is sold. The server's
          tier table also carries `team` and `business` so a second tier is a
          config change rather than a migration, but neither has a store product
          — granting one from here would hand somebody a plan they could never
          buy or renew themselves, and leave the panel as the only way anyone
          ever gets it.
        */}
        {grandfathered && (
          // A grant made before the tier was withdrawn. Listed so the dropdown
          // shows what the account actually has instead of sitting blank next
          // to a badge that says otherwise; disabled so it cannot be given out
          // again, and so switching away from it is a one-way door.
          <option value={grandfathered} disabled>
            {grandfathered}
          </option>
        )}
      </select>
    </div>
  );
}
